import { Button } from 'antd';
import clsx from 'clsx';
import { map, omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { ForwardArrow } from '../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  DEFAULT_PAGE_SIZE,
  ROUTES,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import CanPerform from '../../../../../../components/CanPerform';
import CommonCard from '../../../../../../components/CommonCard';
import CommonTable from '../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../components/CommonTooltip';
import InfiniteScrollHandler from '../../../../../../components/InfiniteScrollHandler';
import SearchComponent from '../../../../../../components/SearchComponent';
import {
  GET_UNASSIGN_AGENCY_LIST,
  GET_UNASSIGN_CONTACT_LIST,
} from '../../../../graphql/Queries';
import AssignAgencyModal from './AssignAgencyModal';

const UnassignAgency = ({ isGlobal = false, globalTeamFilter = [] }) => {
  const {
    navigate,
    params: { projectId },
  } = useRouter();
  const initialProjectAgencyFilter = {
    skip: 0,
    limit: 10,
    projectId,
    ...(globalTeamFilter?.length > 0 &&
      !isGlobal && { projectAgencyIds: globalTeamFilter }),
    sortBy: { field: 'createdAt', order: 'DESC' },
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  };
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [projectAgencyFilter, setProjectAgencyFilter] = useState(
    initialProjectAgencyFilter,
  );
  const [showModal, setShowModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [agencyData, setAgencyData] = useState();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [agencyListData, setAgencyListData] = useState();
  const [hasMore, setHasMore] = useState(true);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [fetchProjectAgency, { loading }] = useLazyQueryWithCallbacks(
    GET_UNASSIGN_AGENCY_LIST,
    {
      variables: {
        filter: projectAgencyFilter,
      },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.unassignAgencyUserList?.data;
        const pagination = {
          ...paginationProp,
          total: res?.unassignAgencyUserList?.total,
        };
        if (scrollFlag) {
          const datalist = [...agencyListData, ...data];
          setAgencyListData(datalist);
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setAgencyListData(datalist);
        }
        setHasMore(!!data?.length);
        setPaginationProp(pagination);
      },
      onError() {},
    },
  );
  const [fetchAgencyContact, { loading: globalContactLoading }] =
    useLazyQueryWithCallbacks(GET_UNASSIGN_CONTACT_LIST, {
      variables: {
        filter: omit(projectAgencyFilter, 'projectId'),
      },
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const data = res?.unassignedUserList?.data;
        const pagination = {
          ...paginationProp,
          total: res?.unassignedUserList?.total,
        };
        if (scrollFlag) {
          const datalist = [...agencyListData, ...data];
          setAgencyListData(datalist);
          setScrollFlag(false);
        } else {
          const datalist = [...data];
          setAgencyListData(datalist);
        }
        setHasMore(!!data?.length);
        setPaginationProp(pagination);
      },
      onError() {},
    });

  useEffect(() => {
    if (isGlobal) {
      fetchAgencyContact({ variables: { filter: projectAgencyFilter } });
    } else {
      fetchProjectAgency({ variables: { filter: projectAgencyFilter } });
    }
  }, []);

  useEffect(() => {
    if (!isGlobal && globalTeamFilter?.length > 0) {
      const newProjectAgencyFilter = {
        ...projectAgencyFilter,
        skip: 0,
      };
      if (globalTeamFilter?.length > 0) {
        newProjectAgencyFilter.projectAgencyIds = globalTeamFilter;
      }
      setProjectAgencyFilter(newProjectAgencyFilter);
      setPaginationProp(initialPaginationValue);
      if (!isGlobal) {
        fetchProjectAgency({
          variables: { filter: newProjectAgencyFilter },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTeamFilter]);

  const refetchAgencyDetails = () => {
    const refreshFilter = {
      ...initialProjectAgencyFilter,
      ...(globalTeamFilter?.length > 0 &&
        !isGlobal && { projectAgencyIds: globalTeamFilter }),
    };
    setPaginationProp(initialPaginationValue);
    setProjectAgencyFilter(refreshFilter);
    if (isGlobal) {
      fetchAgencyContact({
        variables: { filter: refreshFilter },
      });
    } else {
      fetchProjectAgency({
        variables: { filter: refreshFilter },
      });
    }
  };

  const handleTableChange = (pagination, filter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination.pageSize;
    setPaginationProp({ ...paginationProp, ...pagination });
    setSortedInfo(sorter);
    if (sorter?.column) {
      const newFilter = {
        ...projectAgencyFilter,
        skip,
        limit: pagination.pageSize,
        ...(globalTeamFilter?.length > 0 &&
          !isGlobal && { projectAgencyIds: globalTeamFilter }),
      };
      setProjectAgencyFilter(newFilter);
      if (isGlobal) {
        fetchAgencyContact({
          variables: {
            filter: {
              ...newFilter,
              sortBy: {
                field: sorter?.columnKey,
                order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
              },
            },
          },
        });
      } else {
        fetchProjectAgency({
          variables: {
            filter: {
              ...newFilter,
              sortBy: {
                field: sorter?.columnKey,
                order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
              },
            },
          },
        });
      }
    } else {
      const newFilter = {
        ...projectAgencyFilter,
        skip,
        limit: pagination.pageSize,
        ...(globalTeamFilter?.length > 0 &&
          !isGlobal && { projectAgencyIds: globalTeamFilter }),
      };
      setProjectAgencyFilter(newFilter);

      if (isGlobal) {
        fetchAgencyContact({
          variables: {
            filter: newFilter,
          },
        });
      } else {
        fetchProjectAgency({
          variables: {
            filter: newFilter,
          },
        });
      }
    }
  };

  const handleAddEdit = () => {
    setShowModal(true);
  };

  const getActionButtons = (record, index) => {
    const handleEdit = () => {
      setAgencyData(record);
      handleAddEdit();
    };
    return (
      <CanPerform
        action={ALLOWED_ACTION_KEYS.EDIT_PROJECT_AGENCIES}
        type={ALLOWED_ACTION_TYPE.BOTH}
      >
        <CommonTooltip>
          <Button
            shape="round"
            type="primary"
            className={clsx(
              'assign-action-button',
              !isDesktopViewport && 'width-percent-100',
            )}
            id={`assign-agency-button-${index}`}
            onClick={handleEdit}
          >
            Assign
          </Button>
        </CommonTooltip>
      </CanPerform>
    );
  };
  const columns = [
    {
      title: '#',
      key: 'id',
      render: (text, record, index) => {
        return <div>{projectAgencyFilter?.skip + index + 1}</div>;
      },
    },
    ...(isGlobal
      ? [
          {
            title: 'PROJECT NAME',
            key: 'projectName',
            sorter: true,
            sortOrder:
              sortedInfo?.columnKey === 'projectName' && sortedInfo?.order,
            render: (record) => {
              return record?.project?.name;
            },
          },
        ]
      : []),
    {
      title: 'USER NAME',
      key: 'name',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'name' && sortedInfo?.order,
      render: (record) => {
        return isGlobal ? record?.user?.name : record?.name;
      },
    },
    {
      title: 'NUMBER',
      key: 'phoneNo',
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'phoneNo' && sortedInfo?.order,
      render: (record) => {
        return isGlobal ? record?.user?.phoneNo : record?.phoneNo;
      },
    },
    ...(isGlobal
      ? []
      : [
          {
            key: 'actions',
            align: 'right',
            render: (record, _, index) => getActionButtons(record, index),
          },
        ]),
  ];

  const onSearchChange = async (value) => {
    const searchFilter = {
      ...projectAgencyFilter,
      skip: 0,
      search: value,
      ...(globalTeamFilter?.length > 0 &&
        !isGlobal && { projectAgencyIds: globalTeamFilter }),
    };
    setProjectAgencyFilter(searchFilter);
    setPaginationProp(initialPaginationValue);
    fetchProjectAgency({
      variables: { filter: searchFilter },
    });
  };
  const handleRefetch = () => {
    if (isGlobal) {
      fetchAgencyContact({
        variables: {
          filter: {
            skip: agencyListData?.length,
            limit: DEFAULT_PAGE_SIZE,
          },
        },
      });
    } else {
      const refetchFilter = {
        ...projectAgencyFilter,
        skip: agencyListData?.length,
        limit: DEFAULT_PAGE_SIZE,
        ...(globalTeamFilter?.length > 0 && {
          projectAgencyIds: globalTeamFilter,
        }),
      };
      fetchProjectAgency({
        variables: {
          filter: refetchFilter,
        },
      });
    }
  };

  return (
    <>
      {showModal && (
        <AssignAgencyModal
          showModal={showModal}
          setShowModal={setShowModal}
          agencyData={agencyData}
          setAgencyData={setAgencyData}
          refetchAgencyData={refetchAgencyDetails}
        />
      )}

      <div className="project-tab-details">
        <div
          className={`project-tab-details-header ${
            isDesktopViewport ? 'd-flex justify-between align-center' : ''
          }`}
        >
          <div
            className={clsx(
              'd-flex align-center',
              !isDesktopViewport && 'mb-10',
            )}
          >
            <h2
              id="unassign-agency-team-link"
              className={clsx(
                'pointer mr-10 text-primary',
                !isDesktopViewport && 'font-size-16',
              )}
              onClick={() => {
                navigate(
                  isGlobal
                    ? `${ROUTES.SETUP}${ROUTES.AGENCIES}`
                    : `${ROUTES.PROJECTS}/${projectId}${ROUTES.AGENCIES}`,
                );
              }}
            >
              Team
            </h2>
            <h2
              className={clsx(
                'd-flex align-center',
                !isDesktopViewport && 'font-size-16',
              )}
            >
              <ForwardArrow className="mr-10" />
              Assign Users
            </h2>
          </div>
          {!isGlobal && (
            <div className="d-flex align-center">
              <SearchComponent
                id="unassign-agency-search-input"
                className={`search-component ${
                  !isDesktopViewport ? 'width-percent-100' : ''
                }`}
                getData={onSearchChange}
              />
            </div>
          )}
        </div>
        {isDesktopViewport ? (
          <CommonTable
            id="unassign-agency-table"
            columns={columns}
            data={agencyListData || []}
            onChange={handleTableChange}
            paginationConfig={paginationProp}
            rowKey={(obj) => obj?.id}
            loading={loading || globalContactLoading}
            onRow={(record) => {
              return {
                onClick: () =>
                  isGlobal &&
                  navigate(
                    `${ROUTES.PROJECTS}/${record?.project?.id}/teams/unassigned`,
                  ),
              };
            }}
          />
        ) : (
          <InfiniteScrollHandler
            id="unassign-agency-cards-container"
            scrollFlag={scrollFlag}
            loading={loading || globalContactLoading}
            refetchData={handleRefetch}
            setScrollFlag={setScrollFlag}
            hasMore={hasMore}
            dataLength={agencyListData?.length}
            skeletonRows={columns?.length - 3}
          >
            {map(agencyListData, (agency, index) => {
              return (
                <CommonCard
                  key={agency?.id}
                  id={`unassign-agency-card-${index}`}
                  onClick={() =>
                    isGlobal &&
                    navigate(
                      `${ROUTES.PROJECTS}/${agency?.project?.id}/teams/unassigned`,
                    )
                  }
                >
                  <div className="d-flex flex-vertical">
                    <div className="common-card d-flex">
                      <div className="mr-5 fw-medium">{index + 1}.</div>
                      <div>
                        <div className="card-header fw-medium">
                          <span className="text-break">
                            {isGlobal ? agency?.project?.name : agency?.name}
                          </span>
                        </div>
                        <div className="card-content text-secondary mt-15">
                          {isGlobal && (
                            <div className="mb-15">
                              <span className="fw-medium mr-5">User Name:</span>
                              {agency?.user?.name}
                            </div>
                          )}
                          <div>
                            <span className="fw-medium mr-5">User Number:</span>
                            {isGlobal ? agency?.user?.phoneNo : agency?.phoneNo}
                          </div>
                        </div>
                      </div>
                    </div>
                    {!isGlobal && (
                      <div className="d-flex user-action-btn mt-15">
                        {getActionButtons(agency, index)}
                      </div>
                    )}
                  </div>
                </CommonCard>
              );
            })}
          </InfiniteScrollHandler>
        )}
      </div>
    </>
  );
};

export default UnassignAgency;
